import HttpClient from "@lib/http-client";
import { getTTL } from "@views/users/list/common-utils";

const CORE_AUTH_URL = process.env.CORE_AUTH_URL;

/**
 * @memberof Services
 */
class AccountService extends HttpClient {
  /**
   *
   * @param {string} username
   * @param {string} password
   * @returns {object}
   */
  async login(username, password) {
    const data = await this.http.post("/auth/login", {
      username,
      password,
    });
    return data;
  }

  /**
   *
   * @param {string} email
   * @returns {object}
   */
  async sendResetPassword(values) {
    const data = await this.http.post("/auth/reset/send", values);
    return data;
  }

  /**
   *
   * @param {string} userId
   * @returns {boolean}
   */
  async resendInvite(userId) {
    const apiUrl = `/auth/invite/${userId}`;
    return await this.http.post(apiUrl);
  }

  /**
   * @param {string} email
   * @param {string} token
   * @returns {Promise<{ organizationName: string | null, passwordPolicy: object }>}
   */
  async validateResetPassword(email, token) {
    const params = {
      token,
      email,
    };
    const data = await this.http.get(`/auth/reset/password`, { params });
    return data;
  }

  /**
   * Sets the password for the user a recovery code belongs to.
   * @param {{ token: string, password: string, termsAndConditionsVersion?: number, selfSignup?: boolean }} payload
   * @returns {Promise<{ transferSessionId: string | null, redirect: string }>}
   */
  async resetPassword(payload) {
    const data = await this.http.post("/auth/reset/password", payload);
    return data;
  }

  /**
   * Exchanges a single-use transfer token for a dashboard session.
   * @param {string} sessionId - The transfer token from `resetPassword`
   * @returns {Promise<object>} The login response
   */
  async claimTransferSession(sessionId) {
    const data = await this.http.post("/auth/claim", { sessionId });
    return data;
  }

  /**
   * Sends a fresh set-password link. The server answers the same whether or
   * not the email belongs to an account.
   * @param {string} email
   */
  async resendRecoveryCode(email) {
    await this.http.post("/auth/reset/send", { email });
  }

  /**
   *
   * @returns {object}
   */
  async impersonate() {
    const data = await this.http.get("client-services/impersonate/login");
    return data;
  }

  /**
   * Obtains user information
   * @param {string} token - SSO authentication token
   * @returns {object}
   */
  async ssoLogin(token) {
    const url = `${CORE_AUTH_URL}/oauth2/login`;
    const config = token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : {};
    const data = await this.http.get(url, config);
    return data;
  }

  async ssoTransfer() {
    const url = `${CORE_AUTH_URL}/sso/transfer`;
    const data = await this.http.post(url);
    return data;
  }

  async buildSloUrl() {
    const callbackUrl = new URL("/login", document.baseURI).href;
    try {
      const sloEndpointDetails = await this.http.get("/auth/oauth2/slo");

      if (sloEndpointDetails.sloEnabled) {
        const baseUrl = sloEndpointDetails.url;
        const redirectParam = sloEndpointDetails.redirectParam;
        const ssoLogoutUrl = `${baseUrl}?${redirectParam}=${encodeURIComponent(
          callbackUrl
        )}`;

        return ssoLogoutUrl;
      } else {
        return undefined;
      }
    } catch (error) {
      console.log(error);
      return undefined;
    }
  }

  async samlLogout(organizationCode) {
    // Attempt saml logout with given organization first
    if (organizationCode) {
      console.log(JSON.stringify(organizationCode));
      const url = "saml/logout/" + organizationCode;
      const data = await this.http.get(url);

      return data;
    }
  }

  async deleteCodes(payload) {
    await this.http.delete("/auth/login-codes", {
      data: payload,
    });
  }

  /**
   *
   * @returns {array}
   */
  async createLoginCodes(payload) {
    return await this.http
      .post("/auth/login-codes", payload)
      .then((response) =>
        response?.map((value) => ({ ...value, ttl: getTTL(value.expiry) }))
      );
  }

  async fetchMostRecentCodes(params, filterFn) {
    return await this.http
      .get(`/auth/login-codes`, {
        params,
      })
      .then((response) => (filterFn ? filterFn(response) : response));
  }

  /**
   *
   * @returns {URL} redirectUrl
   */
  async proprofsAuth(payload) {
    const data = await this.http.post("/auth/proprofs/login", payload);
    return data;
  }

  /**
   *
   * @returns {object}
   */
  async verify_self_signup() {
    const data = await this.http.get("/auth/self_signup/verify");
    return data;
  }
}

export default AccountService;
